import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { CustomDateAdapter } from '@shared/services/custom-date-adapter';
import {
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { TipoPrioridadeEnum } from '@shared/enums/compras/tipo-prioridade.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraService } from '@shared/services/apis/compras/compra.service';
import { ProdutoService } from '@shared/services/apis/produto/produto.service';
import { UnidadeMedidaService } from '@shared/services/apis/produto/unidade-medida.service';
import { UsuarioService } from '@shared/services/apis/usuario/usuario.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { ProdutoListaRefinamentoEnum } from '@shared/enums/produto/produto-lista-refinamento.enum';
import { SolicitacaoCompraRequest } from '@shared/services/requests/compras/solicitacao-compra.request';
import { ProdutoListStruct } from '@shared/services/structs/produto/produto-list.struct';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '@shared/services/mask.service';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { toApiDate } from '../card-edit-dialog/card-edit-dialog.types';

export interface CatalogProduct {
  idProduto?: number;
  sku: string;
  name: string;
  marca?: string | null;
  unit: string;
  stock: number;
  price: number;
  avulso?: boolean;
  note?: string;
}

export interface RequestItem extends CatalogProduct {
  uid: string;
  qty: number;
}

export interface Person {
  idUsuario: number;
  initials: string;
  name: string;
  color: string;
}

export interface UnidadeOption {
  value: string;
  label: string;
}

const PRIO_OPTS = [
  { key: 'baixa', label: 'Baixa', color: '#4CAF50', idTipoPrioridade: TipoPrioridadeEnum.Baixa },
  { key: 'media', label: 'Média', color: '#FFC107', idTipoPrioridade: TipoPrioridadeEnum.Media },
  { key: 'alta',  label: 'Alta',  color: '#F44336', idTipoPrioridade: TipoPrioridadeEnum.Alta },
];

const AVATAR_COLORS = ['#B73232', '#1E5DB8', '#1E8E3E', '#7A4FBF', '#C26A00', '#0F7A8B'];

@Component({
  selector: 'app-nova-solicitacao-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    SelectPesquisavelComponent,
    EmptyStateComponent,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './nova-solicitacao-dialog.component.html',
  styleUrl: './nova-solicitacao-dialog.component.css',
})
export class NovaSolicitacaoDialogComponent implements OnInit, OnDestroy {
  readonly prioOpts = PRIO_OPTS;
  readonly displayedColumns = ['item', 'quantidade', 'unidade', 'acoes'];

  people: Person[] = [];
  unitOpts: UnidadeOption[] = [];

  items: RequestItem[] = [];
  titulo = '';
  prio = 'media';
  date: Date | null = null;
  setor = '';
  responsavelControl = new FormControl<number | null>(null, Validators.required);
  justification = '';

  // Product search
  itemAdding = false;
  @ViewChild('buscaProdutoInput') private buscaProdutoInput?: ElementRef<HTMLInputElement>;
  @ViewChild('avulsoDescInput') private avulsoDescInput?: ElementRef<HTMLInputElement>;
  searchQuery = '';
  results: CatalogProduct[] = [];
  searching = false;

  // Avulso form
  avulsoMode = false;
  avulsoName = '';
  avulsoUnit = 'un';
  avulsoNote = '';

  saving = false;

  quantidadeMask: ReturnType<MaskService['getDynamicDecimalMask']>;

  private uidSeq = 0;
  private readonly searchInput$ = new Subject<string>();
  private readonly subscriptions = new Subscription();

  constructor(
    private dialogRef: MatDialogRef<NovaSolicitacaoDialogComponent>,
    private produtoService: ProdutoService,
    private usuarioService: UsuarioService,
    private authService: AuthService,
    private unidadeMedidaService: UnidadeMedidaService,
    private compraService: CompraService,
    private alertService: AlertService,
    private maskService: MaskService,
  ) {
    this.quantidadeMask = this.maskService.getDynamicDecimalMask();
  }

  onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  ngOnInit(): void {
    this.carregarResponsaveis();
    this.carregarUnidades();
    this.configurarBuscaProduto();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ----- Data loading -----
  private carregarResponsaveis(): void {
    this.subscriptions.add(
      this.usuarioService.getAll().subscribe({
        next: (res) => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar os responsáveis.', AlertType.error);
            return;
          }
          this.people = (res.listaUsuario || [])
            .filter(u => u.ativo && !u.deletado)
            .map((u, i) => ({
              idUsuario: u.idUsuario,
              name: u.nomeUsuario,
              initials: this.derivarIniciais(u.nomeUsuario),
              color: AVATAR_COLORS[i % AVATAR_COLORS.length],
            }));
          this.preSelecionarResponsavelLogado();
        },
        error: () => this.alertService.show('Erro', 'Não foi possível carregar os responsáveis.', AlertType.error),
      }),
    );
  }

  private preSelecionarResponsavelLogado(): void {
    if (this.responsavelControl.value != null) return;

    const idUsuarioLogado = this.authService.getUserInfos()?.idUsuario ?? null;
    if (!idUsuarioLogado) return;

    if (this.people.some(p => p.idUsuario === idUsuarioLogado)) {
      this.responsavelControl.setValue(idUsuarioLogado);
    }
  }

  private carregarUnidades(): void {
    this.subscriptions.add(
      this.unidadeMedidaService.getLookup().subscribe({
        next: (res) => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar as unidades de medida.', AlertType.error);
            return;
          }
          this.unitOpts = (res.list || []).map(u => ({
            value: this.abreviacaoUnidade(u.descricao),
            label: u.descricao,
          }));
          if (this.unitOpts.length && !this.unitOpts.some(u => u.value === this.avulsoUnit)) {
            this.avulsoUnit = this.unitOpts[0].value;
          }
        },
        error: () => this.alertService.show('Erro', 'Não foi possível carregar as unidades de medida.', AlertType.error),
      }),
    );
  }

  private configurarBuscaProduto(): void {
    this.subscriptions.add(
      this.searchInput$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((texto) => {
            this.searching = true;
            return this.produtoService.getAll({
              searchText: texto,
              idProdutoListaRefinamento: ProdutoListaRefinamentoEnum.Descricao,
              isAtivo: true,
            }, 0).pipe(
              // O erro fica dentro do switchMap: a busca continua viva para o próximo termo.
              catchError(() => {
                this.alertService.show('Erro', 'Não foi possível buscar os produtos.', AlertType.error);
                return of(null);
              }),
            );
          }),
        )
        .subscribe((res) => {
          this.searching = false;
          if (res?.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível buscar os produtos.', AlertType.error);
          }
          const picked = new Set(this.items.filter(i => i.idProduto).map(i => i.idProduto));
          this.results = (!res || res.isError ? [] : (res.list || []))
            .filter(p => !picked.has(p.idProduto))
            .slice(0, 8)
            .map(p => this.mapProduto(p));
        }),
    );
  }

  onSearchChange(): void {
    // Ao escolher uma opção, o mat-autocomplete grava o produto (objeto) no ngModel: só texto digitado busca.
    if (typeof this.searchQuery !== 'string') return;
    this.searchInput$.next((this.searchQuery || '').trim());
  }

  /** Texto que o mat-autocomplete põe no campo ao escolher uma opção (o addItem limpa em seguida). */
  displayProduto = (p: CatalogProduct | string | null): string => (typeof p === 'string' ? p : p?.name ?? '');

  private mapProduto(p: ProdutoListStruct): CatalogProduct {
    return {
      idProduto: p.idProduto,
      sku: p.codigoSku || '',
      name: p.descricao,
      marca: p.marca ?? null,
      unit: p.unidade || 'un',
      stock: p.saldoDisponivel ?? p.saldoFisico ?? 0,
      price: p.preco ?? 0,
    };
  }

  // ----- Derived values -----
  get avulsoValid(): boolean {
    return this.avulsoName.trim().length > 1;
  }

  get valid(): boolean {
    return !this.motivoCriarDesabilitado;
  }

  /** Motivo do "Criar solicitação" desabilitado (tooltip). Vazio = pode criar. */
  get motivoCriarDesabilitado(): string {
    if (!this.items.length) return 'Adicione ao menos um item.';
    if (!this.items.every(i => !!i.qty && +i.qty > 0)) return 'Informe a quantidade de todos os itens.';
    if (!this.responsavelControl.value) return 'Selecione o responsável.';
    return '';
  }

  getPerson(idUsuario: number | null): Person | undefined {
    return idUsuario == null ? undefined : this.people.find(p => p.idUsuario === idUsuario);
  }

  getPrioColor(key: string): string {
    return this.prioOpts.find(o => o.key === key)?.color || '#888';
  }

  getPrioLabel(key: string): string {
    return this.prioOpts.find(o => o.key === key)?.label || key;
  }

  // ----- Item actions -----
  toggleAddingItem(): void {
    this.itemAdding = !this.itemAdding;
    this.avulsoMode = false;
    this.searchQuery = '';
    this.results = [];
    // O campo só existe depois que o @if renderiza o painel.
    if (this.itemAdding) setTimeout(() => this.buscaProdutoInput?.nativeElement.focus());
  }

  /** Vale para o produto do cadastro e para o avulso (confirmAvulso): incluiu, fecha o painel. */
  addItem(p: CatalogProduct): void {
    this.items = [...this.items, { ...p, uid: 'i' + this.uidSeq++, qty: 1 }];
    this.searchQuery = '';
    this.results = [];
    this.itemAdding = false;
  }

  removeItem(index: number): void {
    this.items = this.items.filter((_, i) => i !== index);
  }

  // ----- Avulso -----
  startAvulso(): void {
    this.avulsoMode = true;
    this.avulsoName = (this.searchQuery || '').trim();
    this.avulsoUnit = this.unitOpts[0]?.value || 'un';
    this.avulsoNote = '';
    this.searchQuery = '';
    this.results = [];
    setTimeout(() => this.avulsoDescInput?.nativeElement.focus());
  }

  /** Volta para a busca do cadastro sem fechar o painel. */
  cancelAvulso(): void {
    this.avulsoMode = false;
    setTimeout(() => this.buscaProdutoInput?.nativeElement.focus());
  }

  confirmAvulso(): void {
    if (!this.avulsoValid) return;
    this.addItem({
      sku: '',
      name: this.avulsoName.trim(),
      unit: this.avulsoUnit,
      stock: 0,
      price: 0,
      avulso: true,
      note: this.avulsoNote.trim() || undefined,
    });
    this.avulsoMode = false;
  }

  // ----- Footer -----
  fechar(): void {
    if (this.saving) return;
    this.dialogRef.close(null);
  }

  criar(): void {
    if (!this.valid || this.saving) return;
    const responsavel = this.responsavelControl.value;
    if (!responsavel) return;
    this.saving = true;

    const request: SolicitacaoCompraRequest = {
      titulo: this.titulo.trim() || null,
      idTipoPrioridade: this.prioOpts.find(o => o.key === this.prio)?.idTipoPrioridade ?? TipoPrioridadeEnum.Media,
      idUsuarioResponsavel: responsavel,
      setor: this.setor.trim() || null,
      dataEntregaDesejada: this.date ? toApiDate(this.date) : null,
      justificativa: this.justification.trim() || null,
      itens: this.items.map(i => ({
        idProduto: i.avulso ? null : i.idProduto ?? null,
        codigoSku: i.sku || null,
        nome: i.name,
        marca: i.avulso ? null : (i.marca ?? null),
        isAvulso: !!i.avulso,
        quantidade: +i.qty,
        unidade: i.unit,
        valorUnitario: i.price,
        observacao: i.note || null,
      })),
    };

    this.compraService.criarSolicitacao(request).subscribe({
      next: (res) => {
        this.saving = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível criar a solicitação.', AlertType.error);
          return;
        }
        this.alertService.show('Sucesso', 'Solicitação de compra criada.', AlertType.success);
        this.dialogRef.close({ ...res, responsavelPessoa: this.getPerson(responsavel) });
      },
      error: () => {
        this.saving = false;
        this.alertService.show('Erro', 'Não foi possível criar a solicitação.', AlertType.error);
      },
    });
  }

  // ----- Helpers -----
  private derivarIniciais(nome: string): string {
    const partes = (nome || '').trim().split(/\s+/).filter(Boolean);
    if (partes.length === 0) return '?';
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  }

  private abreviacaoUnidade(descricao: string): string {
    // O lookup retorna "UN - Unidade"; usamos a abreviação antes do " - ".
    const idx = descricao.indexOf(' - ');
    return (idx > 0 ? descricao.slice(0, idx) : descricao).trim();
  }
}
